import React, { useContext, useEffect, useState } from 'react';

import { Close } from '@mui/icons-material';
import { Box, Button, Grid, Modal, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { response } from '../../../api/db';
import {
  getProduct,
  updateProduct,
  saveDraftProduct,
  deleteProduct
} from '../../../api/db/product';
import {
  gray10,
  gray100,
  gray70,
  gray90,
  primaryButtonBackgroundColor,
  primaryButtonOutlinedColor,
  primaryColor,
  tertiaryButtonOutlinedColor
} from '../../../constants/Colors';
import { sideBarWidth, spHeaderHeight } from '../../../constants/Location';
import { AuthContext } from '../../../contexts/auth';
import { method, userType as userTypeEnum } from '../../../constants/Enum';
import { common } from '../../../modules/error';
import { productSchema } from '../../../validation/products';
import type {
  GetPriceHistoryType,
  PriceHistoryType,
  ProductRegisterType
} from '../../../validation/products/productType';

import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import Faq from '../../uiParts/Faq';
import ProductRegisterForm from '../../uiParts/ProductRegisterForm';
import SideBar from '../../uiParts/SideBar';
import CustomHead from '../CustomHead';
import { productStatus, registerKind } from '../../../constants/Enum';
import { error } from '../../../validation/text';
import { AddErrorList } from './ProductRegister';
import { sentryLog } from '../../../config/sentry';

const styles = {
  container: {
    '@media screen and (min-width:961px)': {
      ml: sideBarWidth
    },
    '@media screen and (max-width:600px)': {
      p: '24px 0'
    },
    '@media screen and (max-width:1440px)': {
      flexDirection: 'column'
    },
    '@media screen and (min-width:1441px)': {
      flexDirection: 'row'
    },
    '@media screen and (max-width:961px)': {
      mt: spHeaderHeight
    },
    p: '32px'
  },
  body: {
    '@media screen and (max-width:600px)': {
      minWidth: '340px',
      p: '0 16px'
    },
    '@media screen and (min-width:1441px)': {
      mr: '56px'
    }
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    },
    fontSize: '32px',
    fontWeight: 500,
    color: gray100
  },
  titleContainer: {
    borderBottom: `1px solid ${gray70}`,
    pb: '24px',
    mb: '24px'
  },
  text: {
    fontSize: '16px',
    mb: '48px',
    color: gray100
  },
  button: {
    '@media screen and (max-width:1440px)': {
      display: 'none'
    },
    fontSize: '16px',
    p: '10px 16px',
    ...primaryButtonOutlinedColor
  },
  questionContainer: {
    '@media screen and (min-width:1441px)': {
      width: '520px',
      position: 'sticky',
      top: '32px'
    },
    backgroundColor: gray10,
    '@media screen and (max-width:1440px)': {
      mb: '125px'
    }
  },
  buttonContainer: {
    '@media screen and (min-width:1441px)': {
      display: 'none'
    },
    '@media screen and (min-width:961px)': {
      ml: sideBarWidth
    },
    backgroundColor: 'rgba(255, 255, 255, 0.80)',
    p: '12px 16px',
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 2,
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column'
  },
  spOutlinedButton: {
    fontSize: '16px',
    p: '10px 16px',
    mb: '8px',
    ...primaryButtonOutlinedColor
  },
  spButton: {
    fontSize: '16px',
    fontWeight: 500,
    mb: '11px',
    p: '10px 16px',
    ...primaryButtonBackgroundColor
  },
  deleteButton: {
    color: primaryColor,
    fontSize: '16px',
    fontWeight: 500,
    width: '100%',
    textDecorationLine: 'underline',
    '&:hover': {
      textDecorationLine: 'underline'
    }
  },
  deleteModal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50%',
    maxWidth: '512px',
    minWidth: '294px',
    bgcolor: 'white',
    p: '24px'
  }
};

const ProductEdit: React.FC = () => {
  const { id } = useParams();
  const currentUserType = Number(localStorage.getItem('user_type'));
  const { state } = useLocation();
  const productInfoId = state?.productInfoId;
  const navigate = useNavigate();
  const {
    isLoggedIn,
    setSnackbarOpen,
    setErrorText,
    snackbarOpen,
    errorText,
    userId,
    companyId,
    userRank,
    userType
  } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [productData, setProductData] = useState<ProductRegisterType>();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [draftFlg, setDraftFlg] = useState<boolean>(false);
  const [co2Unit, setCo2Unit] = useState<string>('');
  const [costUnit, setCostUnit] = useState<string>('');

  const {
    control,
    setValue,
    formState: { errors },
    handleSubmit,
    watch
  } = useForm<ProductRegisterType>({
    defaultValues: productData,
    resolver: yupResolver(productSchema)
  });

  // 画面アクセス制限
  useEffect(() => {
    if (!isLoggedIn) navigate('/auth/login');

    // 建材登録ユーザー・事務局ユーザーではない場合は、ホームへリダイレクト
    if (
      currentUserType !== userTypeEnum.admin &&
      currentUserType !== userTypeEnum.maker
    )
      return navigate('/home');
  }, [currentUserType]);

  // 自分の建材ではない場合は、ホームへリダイレクト
  useEffect(() => {
    if (!companyId || !productData) return;
    if (companyId !== productData.companyId) {
      setSnackbarOpen && setSnackbarOpen(true);
      setErrorText &&
        setErrorText('自分の企業が登録した建材以外は編集できません。');
      navigate('/product');
    }
  }, [companyId, productData]);

  const [priceHistory, setPriceHistory] = useState<PriceHistoryType[]>([]);

  const dateFormatter = (date: Date) => {
    const newDate = new Date(date);
    return newDate.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // 登録済みデータ取得
  useEffect(() => {
    window.scrollTo(0, 0);
    void (async () => {
      try {
        setLoading(true);
        if (!id) return;
        const response = await getProduct(
          Number(id),
          method.emissionIntensity,
          productInfoId
        );
        const product = response.data.products;
        setDraftFlg(product.product_info_status === productStatus.draft);
        let images;
        if (product.image.length > 0) {
          images = product.image.map(
            (image: {
              id: string;
              name: string;
              sort: number;
              path: string;
            }) => {
              return {
                id: image.id,
                name: image.name,
                binary: null,
                sort: image.sort,
                filepath: image.path,
                kind: 'exist'
              };
            }
          );
        }
        setProductData({
          userId: product.users.id,
          companyId: product.users.company_id,
          alias: product.alias,
          name: product.name,
          specification: product.specification,
          summary: product.summary,
          condition: product.condition,
          images: images,
          categoryId: product.category.id ?? 0,
          materialId: product.material.id ?? 0,
          calculationSpecification: product.material.calculation_specification,
          emissionIntensity: product.emission_intensity,
          reduceRate: product.reduce_rate,
          calculationSupplement: product.calculation_supplement,
          calculationBasisType: product.calculation_basis_type,
          calculationBasisName: product.calculation_basis_name,
          calculationBasisFileUrl: product.calculation_basis_url,
          carbonOffsetFlg: product.carbon_offset_flg,
          otherCertification: product.other_certification,
          priceCondition: product.material.price_condition,
          referenceUnitPrice: product.reference_unit_price,
          settingAt: product.setting_at,
          priceRemarks: product.price_remarks,
          relatedUrl: product.related_url,
          remark: product.remark,
          assignee: product.users.id,
          document: product.document.name && {
            kind: 'exist',
            name: product.document.name,
            id: product.id
          },
          documentDescriptionUrl: product.document_description_url,
          calculationBasisFile: product.calculation_basis_file.name && {
            kind: 'exist',
            name: product.calculation_basis_file.name,
            id: product.id
          }
        });
        const priceHistories = product.price_histories
          .filter(
            (price: GetPriceHistoryType) => price.reference_unit_price !== null
          )
          .map((price: GetPriceHistoryType) => ({
            productInfoId: price.product_info_id,
            settingAt: price.setting_at
              ? dateFormatter(price.setting_at)
              : null,
            referenceUnitPrice: price.reference_unit_price,
            co2Unit: price.co2_unit,
            costUnit: price.cost_unit
          }));
        setPriceHistory(priceHistories);
        setCo2Unit(product.material.co2_unit);
        setCostUnit(product.material.cost_unit);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          if (setErrorText && setSnackbarOpen) {
            setErrorText(errorResponse.data.message);
            setSnackbarOpen(true);
          }
        } else {
          if (setErrorText && setSnackbarOpen) {
            setErrorText(common);
            setSnackbarOpen(true);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);

  // yupで設定できないエラーを管理
  const [addErrorList, setAddErrorList] = useState<AddErrorList | null>(null);

  // CO2原単位と削減率のどちらか必須
  const co2RequiredCheck = (): boolean => {
    const flg = !watch('emissionIntensity') && !watch('reduceRate');
    setAddErrorList((addErrorList) => ({
      ...addErrorList,
      co2RequiredError: {
        error: flg,
        errorMessage: flg ? error.requiredCo2 : null
      }
    }));
    return flg;
  };

  useEffect(() => {
    firstSubmit && co2RequiredCheck();
  }, [watch('emissionIntensity'), watch('reduceRate')]);

  // CO2算定根拠 種別が第三者認証の時に、名称を必須にする
  const caluclatedNameCheck = (): boolean => {
    const flg =
      Number(watch('calculationBasisType')) === 1 &&
      !watch('calculationBasisName');
    setAddErrorList((addErrorList) => ({
      ...addErrorList,
      co2BasisNameRequiredError: {
        error: flg,
        errorMessage: flg ? error.required : null
      }
    }));
    return flg;
  };

  useEffect(() => {
    firstSubmit && caluclatedNameCheck();
  }, [watch('calculationBasisType'), watch('calculationBasisName')]);

  // 下書きの場合、商品名のみ必須
  const nameRequiredCheck = (): boolean => {
    const flg = !watch('name');
    setAddErrorList((addErrorList) => ({
      ...addErrorList,
      nameRequiredError: {
        error: flg,
        errorMessage: flg ? error.required : null
      }
    }));
    return flg;
  };

  useEffect(() => {
    firstSubmit && nameRequiredCheck();
  }, [watch('name')]);

  const isButtonDisabled = () => {
    if (!firstSubmit) return false;
    return (
      Boolean(errors.alias) ||
      Boolean(errors.name) ||
      Boolean(errors.specification) ||
      Boolean(errors.summary) ||
      Boolean(errors.images) ||
      Boolean(errors.document) ||
      Boolean(errors.documentDescriptionUrl) ||
      Boolean(errors.categoryId) ||
      Boolean(errors.materialId) ||
      Boolean(errors.calculationSpecification) ||
      Boolean(errors.emissionIntensity) ||
      Boolean(errors.reduceRate) ||
      Boolean(errors.calculationSupplement) ||
      Boolean(errors.calculationBasisType) ||
      Boolean(errors.calculationBasisName) ||
      Boolean(errors.calculationBasisFile) ||
      Boolean(errors.calculationBasisFileUrl) ||
      Boolean(errors.otherCertification) ||
      Boolean(errors.referenceUnitPrice) ||
      Boolean(errors.settingAt) ||
      Boolean(errors.priceRemarks) ||
      Boolean(errors.relatedUrl) ||
      Boolean(errors.remark) ||
      Boolean(errors.assignee) ||
      Boolean(addErrorList?.co2BasisNameRequiredError?.error) ||
      Boolean(addErrorList?.co2RequiredError?.error)
    );
  };

  // 編集処理
  const onSubmit = async () => {
    try {
      setLoading(true);
      window.dataLayer.push({
        event: 'save',
        screen: window.location.pathname,
        category: 'button',
        action: 'product_save_edit',
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      if (draftFlg) {
        //  下書き → 登録申請
        const kind = registerKind.create;
        await saveDraftProduct(Number(id), productInfoId, kind, watch);
        navigate('/product/complete', { state: { status: 'register' } });
      } else {
        // 登録済み → 変更申請
        await updateProduct(Number(id), watch);
        navigate('/product/complete', { state: { status: 'update' } });
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        if (setErrorText && setSnackbarOpen) {
          setErrorText(errorResponse.data.message);
          setSnackbarOpen(true);
        }
      } else {
        if (setErrorText && setSnackbarOpen) {
          setErrorText(common);
          setSnackbarOpen(true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // 下書き処理（下書き → 下書き）
  const handleSave = async () => {
    try {
      setLoading(true);
      if (!productInfoId) return;
      await saveDraftProduct(
        Number(id),
        productInfoId,
        registerKind.draft,
        watch
      );
      window.dataLayer.push({
        event: 'draft',
        screen: window.location.pathname,
        category: 'button',
        action: 'product_save_draft',
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      navigate('/product/complete', { state: { status: 'draft' } });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        if (setErrorText && setSnackbarOpen) {
          setErrorText(errorResponse.data.message);
          setSnackbarOpen(true);
        }
      } else {
        if (setErrorText && setSnackbarOpen) {
          setErrorText(common);
          setSnackbarOpen(true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // 削除処理
  const handleDelete = async () => {
    try {
      if (!id) return;
      setOpenDeleteModal(false);
      await deleteProduct(Number(id));
      window.dataLayer.push({
        event: 'delete',
        screen: window.location.pathname,
        category: 'text',
        action: 'product_save_delete',
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      navigate('/product/complete', { state: { status: 'delete' } });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        if (setErrorText && setSnackbarOpen) {
          setErrorText(errorResponse.data.message);
          setSnackbarOpen(true);
        }
      } else {
        if (setErrorText && setSnackbarOpen) {
          setErrorText(common);
          setSnackbarOpen(true);
        }
      }
    }
  };

  useEffect(() => {
    if (productData) {
      Object.keys(productData).forEach((key) => {
        setValue(
          key as keyof ProductRegisterType,
          productData[key as keyof ProductRegisterType]
        );
      });
    }
  }, [productData, setValue]);

  return (
    <>
      <CustomHead title="建材編集 | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Grid container>
        <SideBar />
        <Grid container sx={styles.container} justifyContent={'center'}>
          <Grid item sx={styles.body} xs>
            <Grid
              container
              sx={styles.titleContainer}
              justifyContent="space-between"
            >
              <Typography sx={styles.title}>建材編集</Typography>
              {draftFlg && (
                <Button
                  variant="outlined"
                  sx={styles.button}
                  onClick={async () => {
                    setFirstSubmit(true);
                    const nameRequiredFlg = nameRequiredCheck();
                    !nameRequiredFlg && (await handleSave());
                  }}
                >
                  下書きに保存する
                </Button>
              )}
            </Grid>
            <ProductRegisterForm
              control={control}
              setValue={setValue}
              errors={errors}
              handleSubmit={handleSubmit}
              onSubmit={onSubmit}
              isButtonDisabled={isButtonDisabled()}
              isNew={false}
              draftFlg={draftFlg}
              onClickDeleteButton={() => setOpenDeleteModal(true)}
              watch={watch}
              getCo2Unit={co2Unit}
              getCostUnit={costUnit}
              addErrorList={addErrorList}
              co2RequiredCheck={co2RequiredCheck}
              caluclatedNameCheck={caluclatedNameCheck}
              setFirstSubmit={setFirstSubmit}
              priceHistory={priceHistory}
            />
          </Grid>
          <Grid item>
            <Grid container sx={styles.questionContainer}>
              <Faq />
            </Grid>
          </Grid>
        </Grid>
        <Grid sx={styles.buttonContainer}>
          <Button
            variant="contained"
            sx={styles.spButton}
            onClick={async () => {
              setFirstSubmit(true);
              const co2RequiredFlg = co2RequiredCheck();
              const caluclatedNameFlg = caluclatedNameCheck();
              await handleSubmit(async () => {
                !co2RequiredFlg && !caluclatedNameFlg && (await onSubmit());
              })();
            }}
            disabled={isButtonDisabled()}
          >
            {draftFlg ? '登録申請する' : '変更申請する'}
          </Button>
          {draftFlg && (
            <Button
              variant="outlined"
              sx={styles.spOutlinedButton}
              onClick={async () => {
                setFirstSubmit(true);
                const nameRequiredFlg = nameRequiredCheck();
                !nameRequiredFlg && (await handleSave());
              }}
            >
              下書きに保存する
            </Button>
          )}
          <Button
            sx={styles.deleteButton}
            onClick={() => setOpenDeleteModal(true)}
          >
            この建材を削除する
          </Button>
        </Grid>
        <Modal
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          sx={{ m: '16px' }}
        >
          <Box sx={styles.deleteModal}>
            <Box
              sx={{
                position: 'absolute',
                top: '24px',
                right: '24px',
                width: '24px',
                height: '24px',
                color: gray90
              }}
            >
              <Close onClick={() => setOpenDeleteModal(false)} />
            </Box>
            <Typography
              sx={{
                fontSize: '22px',
                fontWeight: 500,
                color: gray100,
                mb: '24px'
              }}
            >
              建材を削除
            </Typography>
            <Typography sx={{ fontSize: '16px', color: gray100, mb: '40px' }}>
              削除すると元に戻すことはできません。よろしいですか？
            </Typography>
            <Grid
              container
              sx={{ width: '100%' }}
              justifyContent="space-between"
            >
              <Button
                fullWidth
                variant="outlined"
                sx={{
                  ...tertiaryButtonOutlinedColor,
                  fontSize: '16px',
                  height: '43px',
                  width: '50%',
                  maxWidth: '248px',
                  '@media screen and (max-width:600px)': {
                    maxWidth: '140px'
                  }
                }}
                onClick={() => setOpenDeleteModal(false)}
              >
                キャンセル
              </Button>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  ...primaryButtonBackgroundColor,
                  fontSize: '16px',
                  height: '43px',
                  width: '50%',
                  maxWidth: '248px',
                  '@media screen and (max-width:600px)': {
                    maxWidth: '140px'
                  }
                }}
                onClick={handleDelete}
              >
                削除する
              </Button>
            </Grid>
          </Box>
        </Modal>
      </Grid>
      <CustomSnackbar
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen && setSnackbarOpen(false)}
        open={snackbarOpen}
        text={errorText ?? ''}
      />
    </>
  );
};

export default ProductEdit;
